import { Table, Tooltip } from 'antd'
import { SortOrder } from 'antd/lib/table'
import classNames from 'classnames'
import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import { PerAvatarPlus } from '../../components/custom_components/AvatarPlus'
import PerButton from '../../components/custom_components/Button'
import { PerEmpty } from '../../components/custom_components/Empty'
import PerSpacer from '../../components/custom_components/Spacer'
import { tableActionButtons } from '../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import { PerTag } from '../../components/custom_components/Tag'
import { useAuth } from '../../context/auth-context'
import useCompanyRankingSettingsMutations from '../../graphql/company-ranking-settings/hooks/useCompanyRankingSettingsMutations'
import { NameDisplayPreference } from '../../graphql/enum/name-display-preference.enum'
import useHandleEmployeeTags from '../../graphql/ranking/hooks/useHandleEmployeeTag'
import { computeFullname } from '../../shared/nameDisplayPreferencesUtil'
import { Country, EmployeeAndTag, EmployeeDisplayTinyInfo, EmployeeTagType, TalentsSeasonSettingsDto, RoleName } from '../../types'
import { useCultivatingTalentsStore } from './CultivatingTalentsStore'
import { LazyRankingTagHistoryModal } from './LazyRankingTagHistoryModal'
import { RankingTableActionButtons } from './RankingTableActionButtons'
import { rankingUiProps } from './ranking.uiprops'
import { CompanySettingsRankingRemoveLowTagModal } from '../companySettings/ranking/RemoveLowTagModal'
import { useUserManagersRolesByCompany } from '../../graphql/company/hooks/useUserManagerRolesByCompany'
import { AddEntryLevelLowTagAssignmentModal } from '../companySettings/ranking/AddEntryLevelLowTagModal'

type Props = {
  tableData: EmployeeAndTag[]
  companyId: string
  country: Country
  isHRSettings: boolean
  isMessageBoxVisible?: boolean
  isAllCompaniesSettings: boolean
  isYourCompanyView: boolean
}

/**
 * Component created as an original copy of [RankingTable](./RankingTable.tsx) component, in a moment in which it has already diverged from the original version created for phase 1.
 * It already included necessary refactorings in order to be compliant to phase 2.
 * This component uses 'RankingPerformancePopover' component, which has been modified by PER-2863. This leads to an incompatibility w.r.t. phase 1.
 * To safely recover from phase 2 to phase 1, we should restore old version of 'RankingPerformancePopover' and 'RankingTable'
 */
export const TagAssignmentTable: FC<Props> = ({
  tableData,
  companyId,
  isHRSettings,
  country,
  isMessageBoxVisible,
  isAllCompaniesSettings,
  isYourCompanyView,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { removeEmployeeTag, employeeHasFutureEmploymentChange, employeeHasOngoingAppraisal } = useHandleEmployeeTags(
    companyId,
    country,
    undefined,
    undefined,
  )
  const [modalOpenRemoveLow, setModalOpenRemoveLow] = useState<boolean>(false)
  const [modalDataRemoveLow, setModalDataRemoveLow] = useState<EmployeeAndTag | undefined>(undefined)
  const [modalDataAddEntryLevelLow, setModalDataAddEntryLevelLow] = useState<EmployeeAndTag | undefined>(undefined)
  const [modalOpenTagHistory, setModalOpenTagHistory] = useState<boolean>(false)
  const [modalTagHistoryEmployeeId, setModalTagHistoryEmployeeId] = useState<string | undefined>(undefined)
  const [talentsSeasonSettings] = useCultivatingTalentsStore((state) => [state.talentsSeasonSettings], shallow)

  // in parent component TalentsSeasonTab we check for validity of talentsSeasonSettings data
  // and show LoadingAndError if it is undefined, so talentsSeasonSettings! is ok like this
  const settings: TalentsSeasonSettingsDto = talentsSeasonSettings!

  const { tableActionButtonsRowWidth } = tableActionButtons()
  const { tagColor, isPerformanceTag, isImpactOfLossTag, isRiskOfLossTag } = rankingUiProps()

  const { updateRankingDisabled } = useCompanyRankingSettingsMutations(companyId, country as Country)
  const { data: userRolesData } = useUserManagersRolesByCompany(companyId)
  const userRoles: RoleName[] =
    userRolesData && userRolesData.findUserManagerRolesByCompany ? userRolesData.findUserManagerRolesByCompany : []
  const isCompanyManager = userRoles.includes(RoleName.CompanyManager)

  const provideButtonsRowWidth = () => {
    if (!isHRSettings && !isAllCompaniesSettings && !isYourCompanyView) {
      return 3
    } else if (isHRSettings) {
      return 3
    } else {
      return 1
    }
  }

  const onRemoveLowModalCancel = () => {
    setModalOpenRemoveLow(false)
    setModalDataRemoveLow(undefined)
  }

  const onAddEntryLevelLowModalCancel = () => {
    setModalDataAddEntryLevelLow(undefined)
  }

  const onTagHistoryModalCancel = () => {
    setModalOpenTagHistory(false)
    setModalTagHistoryEmployeeId(undefined)
  }

  const employeeTableHeader = <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('rankingView.table.employee.heading')}</Tooltip>

  const columns = [
    {
      key: 'employee',
      title: employeeTableHeader,
      className: 'minWidth-column-xl',
      sorter: (rowDataA: EmployeeAndTag, rowDataB: EmployeeAndTag) => {
        const nameA = computeFullname(user.nameDisplayPreference, rowDataA.firstName, rowDataA.lastName)
        const nameB = computeFullname(user.nameDisplayPreference, rowDataB.firstName, rowDataB.lastName)
        return nameA.localeCompare(nameB)
      },
      render: (data: EmployeeAndTag) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={data.id}
          firstName={data.firstName}
          lastName={data.lastName}
          infoText={t(`employee.contract.level.${data.currentLevel}`)}
          linkToProfile={
            (isCompanyManager || isAllCompaniesSettings || isHRSettings || isYourCompanyView) && data.id !== user.employeeId
              ? {
                  resigned: false,
                }
              : undefined
          }
          tooltip={
            (isCompanyManager || isAllCompaniesSettings || isHRSettings || isYourCompanyView) && data.id !== user.employeeId
              ? t('employeeList.table.viewProfile')
              : undefined
          }
        />
      ),
    },
    {
      key: 'bu',
      title: t('rankingView.table.businessUnit.heading'),
      className: 'minWidth-column-m direction-column',
      sorter: (employeeA: EmployeeAndTag, employeeB: EmployeeAndTag) => {
        return employeeA.buName.localeCompare(employeeB.buName)
      },
      render: (rowData: EmployeeAndTag) => rowData.buName,
    },
    {
      key: 'performance',
      title: t('rankingView.table.performance.heading'),
      className: 'minWidth-column-l',
      defaultSortOrder: 'ascend' as SortOrder,
      sorter: (A: EmployeeAndTag, B: EmployeeAndTag) => {
        const sortColData = (data: EmployeeAndTag) => {
          const tags = data.tags.filter((tag) => isPerformanceTag(tag.type as EmployeeTagType))
          if (tags.length === 0) {
            return 0
          }
          return (
            tags
              .map((tag) => {
                if (tag.type === EmployeeTagType.Top) {
                  return tags.length > 1 ? -10 * tags.length : -5
                } else if (tag.type === EmployeeTagType.Expert) {
                  return -4
                } else if (tag.type === EmployeeTagType.Sales) {
                  return -3
                } else if (tag.type === EmployeeTagType.Speaker) {
                  return -2
                } else if (tag.type === EmployeeTagType.Coach) {
                  return -1
                } else if (tag.type === EmployeeTagType.Low || tag.type === EmployeeTagType.EntryLevelLow) {
                  return 1
                } else {
                  return 2
                }
              })
              .reduce((c, a) => c + a, 0) / Math.max(1, tags.length)
          )
        }
        return sortColData(A) - sortColData(B)
      },
      render: (rowData: EmployeeAndTag) => {
        return (
          <div>
            {rowData.tags &&
              rowData.tags.length > 0 &&
              rowData.tags
                .filter((t) => isPerformanceTag(t.type as EmployeeTagType))
                .map((tag, index) => {
                  return (
                    <PerSpacer key={index} my="xxxs" display="inline-block">
                      <PerTag color={tagColor(tag.type)}>{t(`global.employeeTags.performance.${tag.type}`)}</PerTag>
                    </PerSpacer>
                  )
                })}
          </div>
        )
      },
    },
    {
      key: 'impact-of-loss',
      title: t('rankingView.table.impactOfLoss.heading'),
      className: 'minWidth-column-s',
      sorter: (A: EmployeeAndTag, B: EmployeeAndTag) => {
        const sortColData = (data: EmployeeAndTag) => {
          const tags = data.tags.filter((tag) => isImpactOfLossTag(tag.type as EmployeeTagType))
          if (tags.length === 0) {
            return 0
          }
          return (
            tags
              .map((tag) => {
                if (tag.type === EmployeeTagType.ImpactOfLossCritical) {
                  return -4
                } else if (tag.type === EmployeeTagType.ImpactOfLossSignificant) {
                  return -3
                } else if (tag.type === EmployeeTagType.ImpactOfLossModerate) {
                  return -2
                } else if (tag.type === EmployeeTagType.ImpactOfLossMinor) {
                  return -1
                } else {
                  return 1
                }
              })
              .reduce((c, a) => c + a, 0) / Math.max(1, tags.length)
          )
        }
        return sortColData(A) - sortColData(B)
      },
      render: (rowData: EmployeeAndTag) => {
        return (
          <>
            {rowData.tags &&
              rowData.tags
                .filter((t) => isImpactOfLossTag(t.type as EmployeeTagType))
                .map((tag, index) => {
                  return (
                    <React.Fragment key={index}>
                      <PerTag color={tagColor(tag.type)}>{t(`global.employeeTags.impactOfLoss.${tag.type}`)}</PerTag>
                    </React.Fragment>
                  )
                })}
          </>
        )
      },
    },
    {
      key: 'risk-of-loss',
      title: t('rankingView.table.riskOfLoss.heading'),
      className: 'minWidth-column-s',
      sorter: (A: EmployeeAndTag, B: EmployeeAndTag) => {
        const sortColData = (data: EmployeeAndTag) => {
          const tags = data.tags.filter((tag) => isRiskOfLossTag(tag.type as EmployeeTagType))
          if (tags.length === 0) {
            return 0
          }
          return (
            tags
              .map((tag) => {
                if (tag.type === EmployeeTagType.RiskOfLossVeryHigh) {
                  return -4
                } else if (tag.type === EmployeeTagType.RiskOfLossHigh) {
                  return -3
                } else if (tag.type === EmployeeTagType.RiskOfLossMedium) {
                  return -2
                } else if (tag.type === EmployeeTagType.RiskOfLossLow) {
                  return -1
                } else {
                  return 1
                }
              })
              .reduce((c, a) => c + a, 0) / Math.max(1, tags.length)
          )
        }
        return sortColData(A) - sortColData(B)
      },
      render: (rowData: EmployeeAndTag) => {
        return (
          <>
            {rowData.tags &&
              rowData.tags
                .filter((t) => isRiskOfLossTag(t.type as EmployeeTagType))
                .map((tag, index) => {
                  return (
                    <React.Fragment key={index}>
                      <PerTag color={tagColor(tag.type)}>{t(`global.employeeTags.riskOfLoss.${tag.type}`)}</PerTag>
                    </React.Fragment>
                  )
                })}
          </>
        )
      },
    },
    {
      key: 'actions',
      title: t('rankingView.table.actions.heading'),
      className: 'textAlign-left actions-small',
      width: tableActionButtonsRowWidth(provideButtonsRowWidth()),
      render: (rowData: EmployeeAndTag) => {
        const employeeFullName = computeFullname(user.nameDisplayPreference, rowData.firstName, rowData.lastName)

        return (
          <RankingTableActionButtons
            employeeFullName={employeeFullName}
            isRankingEnabled={false}
            employeeAndTagData={rowData}
            settingsTopPercentage={settings.topPercentage}
            isHRSettings={isHRSettings}
            isAllCompaniesSettings={isAllCompaniesSettings}
            isYourCompanyView={isYourCompanyView}
            isCompanyManager={isCompanyManager}
            setModalOpenRemoveLow={setModalOpenRemoveLow}
            setModalDataRemoveLow={setModalDataRemoveLow}
            setModalDataAddEntryLevelLow={setModalDataAddEntryLevelLow}
            setModalOpenTagHistory={(open: boolean) => {
              setModalOpenTagHistory(open)
              setModalTagHistoryEmployeeId(rowData.id)
            }}
            companyId={companyId}
            country={country}
          />
        )
      },
    },
  ]

  return (
    <>
      {modalDataRemoveLow && (
        <CompanySettingsRankingRemoveLowTagModal
          isOpen={modalOpenRemoveLow}
          onSubmit={removeEmployeeTag}
          modalData={modalDataRemoveLow}
          onCancel={onRemoveLowModalCancel}
        />
      )}
      {modalDataAddEntryLevelLow && (
        <AddEntryLevelLowTagAssignmentModal
          onClose={onAddEntryLevelLowModalCancel}
          employeeFullName={computeFullname(
            user.nameDisplayPreference,
            modalDataAddEntryLevelLow.firstName,
            modalDataAddEntryLevelLow.lastName,
          )}
          employeeFirstName={modalDataAddEntryLevelLow.firstName}
          employeeLastName={modalDataAddEntryLevelLow.lastName}
          employeeHasFutureEmploymentChange={employeeHasFutureEmploymentChange}
          employeeHasOngoingAppraisal={employeeHasOngoingAppraisal}
          companyId={companyId}
          country={country}
          employeeId={modalDataAddEntryLevelLow.id}
        />
      )}
      {modalTagHistoryEmployeeId && (
        <LazyRankingTagHistoryModal
          isOpen={modalOpenTagHistory}
          onCancel={onTagHistoryModalCancel}
          employee={tableData.filter((e) => e.id === modalTagHistoryEmployeeId)[0] as EmployeeDisplayTinyInfo}
        />
      )}

      <Table
        className={classNames('scroll-compensation light small', {
          viewWithTabsAndInfoBox: !isHRSettings,
          viewWithDoubleTabsAndInfoBox: isHRSettings,
          'light viewWithTabsAndInfoBoxAndMessageBoxAndConfirmButton small': isMessageBoxVisible,
        })}
        rowClassName={() => 'minHeight-row'}
        dataSource={tableData.map((o) => ({ ...o, key: o.id }))}
        columns={columns}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: (
            <PerEmpty
              icon
              heading={t('rankingView.table.disabled.headingWithoutYear')}
              info={isHRSettings ? t('rankingView.table.disabled.infoWithoutYear') : undefined}
              button={
                isHRSettings && (
                  <PerButton
                    text={t('rankingView.table.disabled.btn')}
                    onClick={() => updateRankingDisabled(false)}
                    color="success"
                    before="check_stroke"
                  />
                )
              }
            />
          ),
        }}
      />
    </>
  )
}
